import React, { useEffect } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { loadWorkshopType, loadWorkshopHome } from '../../ducks/workshop.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import WorkshopCollection from './WorkshopCollection';
import WorkshopSubnav from './WorkshopSubnav';
import css from './WorkshopPage.module.css';

// Trailer-type hero art keyed by slug (reuse homepage set; falls back to Strapi heroImage).
import imgCarHauler from '../../assets/workshop/trailer-car-hauler.jpg';
import imgDump from '../../assets/workshop/trailer-dump.jpg';
import imgEnclosed from '../../assets/workshop/trailer-enclosed.jpg';
import imgUtility from '../../assets/workshop/trailer-utility.jpg';
import imgEquipment from '../../assets/workshop/trailer-equipment.jpg';
import imgGooseneck from '../../assets/workshop/trailer-gooseneck.jpg';

// Keyed by the REAL Strapi trailer-type slugs (all 6).
const TRAILER_IMG = {
  'car-hauler': imgCarHauler,
  'dump-trailer': imgDump,
  'enclosed-trailer': imgEnclosed,
  'utility-trailer': imgUtility,
  'equipment-trailer': imgEquipment,
  'gooseneck-trailer': imgGooseneck,
};

const WorkshopTypePageComponent = props => {
  const { scrollingDisabled, config } = props;
  const dispatch = useDispatch();
  const params = useParams();
  const slug = params?.typeSlug || params?.slug || '';

  const { currentType, typeAccessories, typeError, categories } = useSelector(
    state => state.workshop
  );

  useEffect(() => {
    dispatch(loadWorkshopType(slug));
  }, [slug]); // eslint-disable-line

  useEffect(() => {
    if (!categories || categories.length === 0) dispatch(loadWorkshopHome());
  }, []); // eslint-disable-line

  const sortedCats = [...(categories || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const marketplaceName = config?.marketplaceName || 'BigLoads';
  const siteUrl = config?.marketplaceRootURL || 'https://bigloads.com';
  const pageUrl = `${siteUrl}/trailer-accessories/${slug}`;

  if (typeError && !currentType) {
    return <NotFoundPage />;
  }

  const t = currentType || {};
  const accessories = typeAccessories || [];
  const typeName = t.name || 'Trailer';

  const seoTitle =
    t.seoTitle || `${typeName} Accessories (2026) — The Workshop | ${marketplaceName}`;
  const seoDescription =
    t.seoDescription ||
    `Owner-tested accessories for your ${typeName.toLowerCase()}. Hitches, tie-downs, lighting, security and more. Browse specs and buy direct.`;

  const subtitle =
    t.shortDesc ||
    `Gear and upgrades picked for ${typeName.toLowerCase()} owners.`;

  // Supporting paragraph under the lede (strip HTML if introCopy is rich text).
  const heroBody =
    (t.introCopy ? t.introCopy.replace(/<[^>]+>/g, '') : '') ||
    `From first-haul essentials to the upgrades that keep a ${typeName.toLowerCase()} earning, every pick is matched to the rig and linked straight to the retailer.`;

  const heroImg = TRAILER_IMG[slug] || t.heroImage || null;

  // ── Structured data (preserved) ─────────────────────────────────────────────
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'The Workshop',
        item: `${siteUrl}/trailer-accessories`,
      },
      { '@type': 'ListItem', position: 3, name: typeName, item: pageUrl },
    ],
  };
  const itemListSchema = {
    '@type': 'ItemList',
    name: seoTitle,
    itemListElement: accessories.slice(0, 20).map((a, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: a.name,
      url: `${siteUrl}/trailer-accessories/product/${a.slug}`,
    })),
  };
  const schemas = [breadcrumbSchema, itemListSchema];

  const crumbs = [
    { name: 'Home', to: { name: 'LandingPage' } },
    { name: 'The Workshop', to: { name: 'WorkshopPage' } },
    { name: typeName },
  ];

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={seoTitle}
      description={seoDescription}
      canonicalUrl={pageUrl}
      schema={schemas}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={true} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <WorkshopSubnav categories={sortedCats} css={css} />
        <WorkshopCollection
          accessories={accessories}
          crumbs={crumbs}
          title={`${typeName} Gear`}
          subtitle={subtitle}
          heroBody={heroBody}
          heroImg={heroImg}
          source="type_page"
          contextSlug={slug}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

WorkshopTypePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const EnhancedWorkshopTypePage = props => {
  const config = useConfiguration();
  return <WorkshopTypePageComponent config={config} {...props} />;
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const WorkshopTypePage = compose(connect(mapStateToProps))(EnhancedWorkshopTypePage);

// SSR loader (named export for pageDataLoadingAPI)
export const loadData = (params, search, config) => dispatch => {
  const slug = params?.typeSlug || params?.slug || '';
  return dispatch(loadWorkshopType(slug));
};

export default WorkshopTypePage;
